// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useStateValue } from '@shared/ui-kit/providers/state-provider';
import {
  type DependencyList,
  type Dispatch,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react';
import { isUndefined } from '@shared/utils';

export interface TabsController {
  readonly id: string;
  readonly tab: string | undefined;
  readonly setTab: Dispatch<string>;
  readonly stateId?: string | undefined;
}

export const useTabsController = (
  initialState: string | undefined | (() => string | undefined),
  stateId?: string | undefined,
  onTabChange?: (value: string) => void,
  deps: DependencyList = [],
): TabsController => {
  const id = useId();

  const [tabsState, setTabsState] = useStateValue<string | undefined>(
    stateId ? `tabs-controller:${stateId}` : undefined,
  );

  const [tab, setTab] = useState(tabsState ?? initialState);

  const onTabValueChange: Dispatch<React.SetStateAction<string | undefined>> =
    useCallback(
      value => {
        setTab(value);

        setTabsState(value);
      },
      [setTabsState],
    );

  useEffect(() => {
    if (!isUndefined(tab)) onTabChange?.(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  /* istanbul ignore next */
  useEffect(() => {
    if (deps.length === 0) return;

    const nextState = tabsState
      ? tabsState
      : typeof initialState === 'function'
      ? initialState()
      : initialState;

    if (tab !== nextState && nextState !== 'undefined') onTabValueChange(nextState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    id,
    tab,
    setTab: onTabValueChange,
    stateId,
  };
};
